import {
  ehFisica,
  NOME_ESTADO_CIVIL,
  nomeDe,
  type Pessoa,
  type Natureza,
} from '@social/shared';

/**
 * Peças compartilhadas entre a consulta de pessoas, o cartão e o cadastro.
 *
 * Espelha `sociedade-comum.ts` de propósito: quem já leu aquele encontra as
 * mesmas peças com os mesmos nomes, e a tela de pessoas não inventa um segundo
 * vocabulário para as mesmas ideias.
 */

/** CPF formatado. O dado guardado é só dígito — a máscara é da tela. */
export const cpfBR = (digitos: string | undefined): string =>
  digitos && digitos.length === 11
    ? digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    : '—';

/** CNPJ formatado, para a pessoa jurídica. */
export const cnpjBR = (digitos: string | undefined): string =>
  digitos && digitos.length === 14
    ? digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    : '—';

/**
 * O documento da pessoa, seja qual for a natureza.
 *
 * Existe porque a tabela tem UMA coluna "Documento": duas colunas (CPF e
 * CNPJ) ficariam meio vazias em qualquer lista com os dois tipos, e a pessoa
 * teria de olhar as duas para achar um número.
 */
export function documentoDe(p: Pessoa): string {
  return ehFisica(p) ? cpfBR(p.cpf) : cnpjBR(p.cnpj);
}

/** Só os dígitos, para a busca casar com o que a pessoa digita com pontuação. */
export function digitosDoDocumento(p: Pessoa): string {
  return ehFisica(p) ? p.cpf : p.cnpj;
}

/** Município/UF da pessoa — o endereço mora em `endereco` nas duas naturezas. */
export function localDe(p: Pessoa): string {
  return `${p.endereco.municipio}/${p.endereco.uf}`;
}

/**
 * A linha de apoio do cartão e da tabela.
 *
 * PF mostra profissão e estado civil; PJ não tem nem um nem outro, e mostrar
 * "—" em duas colunas para toda pessoa jurídica seria ruído. Uma função só
 * decide o que cabe, em vez de cada tela repetir o `if`.
 */
export function qualificacaoDe(p: Pessoa): string {
  return ehFisica(p)
    ? `${p.profissao}, ${NOME_ESTADO_CIVIL[p.estadoCivil].toLowerCase()}`
    : 'pessoa jurídica';
}

export const NOME_NATUREZA: Record<Natureza, string> = {
  FISICA: 'Pessoa física',
  JURIDICA: 'Pessoa jurídica',
};

/** Idade em anos completos. `undefined` para PJ, que não tem nascimento. */
export function idadeDe(p: Pessoa, hoje = new Date()): number | undefined {
  if (!ehFisica(p)) return undefined;
  const [ano, mes, dia] = p.nascimento.split('-').map(Number);
  if (!ano || !mes || !dia) return undefined;
  let idade = hoje.getFullYear() - ano;
  // O mês do JS é zero-based; o do ISO não. Comparar sem ajustar erra o
  // aniversário do mês corrente por um mês inteiro.
  const jaFezAniversario =
    hoje.getMonth() + 1 > mes || (hoje.getMonth() + 1 === mes && hoje.getDate() >= dia);
  if (!jaFezAniversario) idade -= 1;
  return idade;
}

/** O nome pelo qual a pessoa é procurada — reexportado para a tela não importar de dois lugares. */
export { nomeDe, ehFisica };
